import { ClientPrintClient } from './fo.api.service';

export const resolveApiHost = (): string => {
  const configured = window.config?.VITE_API_HOST;
  if (configured && configured !== 'undefined' && configured !== 'null') {
    return configured.replace(/\/+$/, '');
  }
  // Fall back to the dashboard's own origin so reverse-proxied deployments
  // (e.g. Caddy/Nginx routing /api/* to the API) keep working without an
  // explicitly configured host.
  return window.location.origin.replace(/\/+$/, '');
};

// The TLClientPrint desktop app is launched by the OS from the `tlprint:` protocol handler and
// issues a bare GET, so it cannot send an Authorization header. The API therefore gates the
// WebPrintJob endpoints on a short-lived (~2 min) single-use token that we, running authenticated
// in the browser, mint just before handing the URL over. The desktop client is unchanged: it still
// just fetches whatever URL it is given.
//
// Consequence: the builders below are async. Await them at the call site, e.g.
//   window.location.href = await buildProPrintJobUrl(id);

/** Job kinds understood by GET /ClientPrint/GetPrintJobToken. Must match ClientPrintController.PrintJobKinds. */
type PrintJobKind = 'pro' | 'manifest' | 'trackingUnit' | 'guideLabel' | 'orphanLabels';

// Read the bearer token the same way auth.module bootstraps itself, so these builders stay usable
// from plain modules and components without threading the token through every signature.
const resolveAuthToken = (): string => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.token || '';
  } catch {
    return '';
  }
};

const requestPrintJobToken = async (
  job: PrintJobKind,
  arg1?: string,
  arg2?: string,
): Promise<string> => {
  const query = new URLSearchParams({ job });
  if (arg1 !== undefined) query.set('arg1', arg1);
  if (arg2 !== undefined) query.set('arg2', arg2);

  const authToken = resolveAuthToken();
  const response = await fetch(`${resolveApiHost()}/ClientPrint/GetPrintJobToken?${query}`, {
    method: 'GET',
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
  });

  if (!response.ok) {
    throw new Error(
      `Unable to authorize this print job (${response.status}). Please sign in again and retry.`,
    );
  }

  const body = await response.json();
  const printToken = body?.token || body?.Token;
  if (!printToken) {
    throw new Error('The server did not return a print token for this job.');
  }
  return printToken;
};

const buildPrintUrl = async (
  path: string,
  job: PrintJobKind,
  arg1?: string,
  arg2?: string,
): Promise<string> => {
  const printToken = await requestPrintJobToken(job, arg1, arg2);
  return `tlprint:${resolveApiHost()}${path}?token=${encodeURIComponent(printToken)}`;
};

export const buildProPrintJobUrl = (proNumber: string): Promise<string> =>
  buildPrintUrl(
    `/ClientPrint/GetProPrintJob/${encodeURIComponent(proNumber)}`,
    'pro',
    proNumber,
  );

export const buildManifestPrintJobUrl = (manifestId: string): Promise<string> =>
  buildPrintUrl(
    `/ClientPrint/GetManifestPrintJob/${encodeURIComponent(manifestId)}`,
    'manifest',
    manifestId,
  );

export const buildTrackingUnitPrintJobUrl = (proNumber: string, barcode: string): Promise<string> =>
  buildPrintUrl(
    `/ClientPrint/GetTrackingUnitPrintJob/${encodeURIComponent(proNumber)}/${encodeURIComponent(barcode)}`,
    'trackingUnit',
    proNumber,
    barcode,
  );

export const buildGuideLabelUrl = (): Promise<string> =>
  buildPrintUrl('/ClientPrint/GetGuideLabel', 'guideLabel');

export const buildOrphanLabelsPrintJobUrl = (numLabels: number): Promise<string> =>
  buildPrintUrl(
    `/ClientPrint/GetOrphanLabels/${encodeURIComponent(String(numLabels))}`,
    'orphanLabels',
    String(numLabels),
  );

const buildPdfUrl = (path: string): string => `${resolveApiHost()}${path}`;

export const buildProLabelPdfUrl = (proNumber: string): string =>
  buildPdfUrl(`/ClientPrint/GetProLabelPdf/${encodeURIComponent(proNumber)}`);

export const buildManifestLabelPdfUrl = (manifestId: string): string =>
  buildPdfUrl(`/ClientPrint/GetManifestLabelPdf/${encodeURIComponent(manifestId)}`);

export const buildTrackingUnitLabelPdfUrl = (proNumber: string, barcode: string): string =>
  buildPdfUrl(
    `/ClientPrint/GetTrackingUnitLabelPdf/${encodeURIComponent(proNumber)}/${encodeURIComponent(barcode)}`,
  );

// Bill of Lading / PRO document (one-page PDF) for a pro.
export const buildProBolPdfUrl = (proNumber: string): string =>
  buildPdfUrl(`/ClientPrint/GetProBolPdf/${encodeURIComponent(proNumber)}`);

export const downloadLabelPdf = async (
  url: string,
  fallbackName: string,
  token: string,
): Promise<void> => {
  const response = await fetch(url, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.text();
      if (body) detail = body;
    } catch {
      /* swallow */
    }
    throw new Error(`Failed to download PDF (${response.status}): ${detail}`);
  }

  const disposition = response.headers.get('content-disposition') || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

class PrintService {

  client = null;

  constructor(token) {
    this.client = new ClientPrintClient(resolveApiHost());
    this.client.setAuthToken(token);
  }

  // Fetched directly rather than through the generated ClientPrintClient: the WebPrintJob
  // endpoint is gated on a single-use print token in the query string, which the generated
  // client has no way to supply. Signature is unchanged for callers.
  async printProLabels(id) {
    const printToken = await requestPrintJobToken('pro', id);
    const response = await fetch(
      `${resolveApiHost()}/ClientPrint/GetProPrintJob/${encodeURIComponent(id)}?token=${encodeURIComponent(printToken)}`,
      { method: 'GET' },
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch print job (${response.status}): ${response.statusText}`);
    }
    return response.text();
  }
}

export default PrintService;
